import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import api from '../lib/api';
import GoldButton from './GoldButton';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Plus, Edit, Trash2, Eye, X } from 'lucide-react';
import { toast } from 'sonner';

export default function TemplatesManagement() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'preview'
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    asunto: '',
    contenido_html: '',
    variables: []
  });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/admin/templates?limit=100');
      // La respuesta ahora tiene formato: { data: [...], total, skip, limit, has_more }
      const templatesData = response.data.data || response.data;
      setTemplates(templatesData);
    } catch (error) {
      toast.error('Error al cargar plantillas');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (template = null) => {
    if (template) {
      setFormData({
        nombre: template.nombre,
        descripcion: template.descripcion,
        asunto: template.asunto,
        contenido_html: template.contenido_html,
        variables: template.variables || []
      });
      setEditingId(template.id);
    } else {
      setFormData({
        nombre: '',
        descripcion: '',
        asunto: '',
        contenido_html: '',
        variables: []
      });
      setEditingId(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      nombre: '',
      descripcion: '',
      asunto: '',
      contenido_html: '',
      variables: []
    });
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/admin/templates/${editingId}`, formData);
        toast.success('Plantilla actualizada exitosamente');
      } else {
        await api.post('/admin/templates', formData);
        toast.success('Plantilla creada exitosamente');
      }
      handleCloseModal();
      fetchTemplates();
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Error al guardar plantilla');
      }
    }
  };

  const handleDelete = async (id, esSistema) => {
    if (esSistema) {
      toast.error('No se pueden eliminar plantillas del sistema');
      return;
    }

    if (!window.confirm('¿Estás seguro de eliminar esta plantilla?')) {
      return;
    }

    try {
      await api.delete(`/admin/templates/${id}`);
      toast.success('Plantilla eliminada');
      fetchTemplates();
    } catch (error) {
      toast.error('Error al eliminar plantilla');
    }
  };

  const handlePreview = (template) => {
    setSelectedTemplate(template);
    setViewMode('preview');
  };

  const handleSeedTemplates = async () => {
    if (!window.confirm('¿Inicializar las plantillas por defecto del sistema? Las plantillas existentes no serán afectadas.')) {
      return;
    }
    
    try {
      const response = await api.post('/admin/templates/seed');
      toast.success(response.data.message);
      fetchTemplates();
    } catch (error) {
      toast.error('Error al inicializar plantillas');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-[#F5A623]">Cargando plantillas...</div>
      </div>
    );
  }

  if (viewMode === 'preview' && selectedTemplate) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-white">Vista Previa: {selectedTemplate.nombre}</h2>
          <GoldButton onClick={() => setViewMode('list')} variant="secondary">
            <X className="w-4 h-4 mr-2" />
            Cerrar
          </GoldButton>
        </div>
        
        <div className="bg-[#2A2A2A] border border-white/10 rounded-lg p-6">
          <div className="mb-4">
            <p className="text-[#666666] text-sm mb-1">Asunto del correo:</p>
            <p className="text-white font-semibold">{selectedTemplate.asunto}</p>
          </div>
          
          <div className="mb-4">
            <p className="text-[#666666] text-sm mb-2">Variables disponibles:</p>
            <div className="flex flex-wrap gap-2">
              {selectedTemplate.variables.map((v, i) => (
                <span key={i} className="px-2 py-1 bg-[#F5A623]/20 text-[#F5A623] rounded text-xs font-mono">
                  {`{${v}}`}
                </span>
              ))}
            </div>
          </div>

          <div className="border border-white/10 rounded-lg p-4 bg-[#1A1A1A]">
            <p className="text-[#666666] text-sm mb-2">Contenido HTML:</p>
            <div 
              className="prose prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: selectedTemplate.contenido_html }} 
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <p className="text-[#666666]">
          Gestiona las plantillas de correo para comunicación con clientes
        </p>
        <div className="flex gap-3">
          {templates.length === 0 && (
            <GoldButton 
              onClick={handleSeedTemplates} 
              variant="secondary"
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Inicializar Plantillas
            </GoldButton>
          )}
          <GoldButton onClick={() => handleOpenModal()}>
            <Plus className="w-4 h-4 mr-2" />
            Nueva Plantilla
          </GoldButton>
        </div>
      </div>

      {templates.length === 0 && (
        <div className="bg-[#2A2A2A] border border-white/10 rounded-lg p-12 text-center">
          <p className="text-[#666666] text-lg mb-2">No hay plantillas disponibles</p>
          <p className="text-[#666666] text-sm">Haz clic en "Inicializar Plantillas" para cargar las plantillas del sistema</p>
        </div>
      )}

      <div className="grid gap-4">
        {templates.map((template) => (
          <motion.div
            key={template.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#2A2A2A] border border-white/10 rounded-lg p-6 hover:border-[#F5A623]/50 transition-colors"
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-white font-bold text-lg">{template.nombre}</h3>
                  {template.es_sistema && (
                    <span className="px-2 py-1 bg-[#10B981]/20 text-[#10B981] rounded text-xs font-semibold">
                      Sistema
                    </span>
                  )}
                  {!template.activo && (
                    <span className="px-2 py-1 bg-[#EF4444]/20 text-[#EF4444] rounded text-xs font-semibold">
                      Inactiva
                    </span>
                  )}
                </div>
                <p className="text-[#666666] text-sm mb-2">{template.descripcion}</p>
                <p className="text-[#F5A623] text-xs mb-2">
                  <strong>Asunto:</strong> {template.asunto}
                </p>
                {template.variables && template.variables.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {template.variables.map((v, i) => (
                      <span key={i} className="px-2 py-1 bg-[#F5A623]/10 text-[#F5A623] rounded text-xs font-mono">
                        {`{${v}}`}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => handlePreview(template)}
                  className="p-2 bg-[#3B82F6]/20 text-[#3B82F6] rounded hover:bg-[#3B82F6]/30 transition-colors"
                  title="Vista previa"
                >
                  <Eye className="w-4 h-4" />
                </button>
                {!template.es_sistema && (
                  <>
                    <button
                      onClick={() => handleOpenModal(template)}
                      className="p-2 bg-[#F5A623]/20 text-[#F5A623] rounded hover:bg-[#F5A623]/30 transition-colors"
                      title="Editar"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(template.id, template.es_sistema)}
                      className="p-2 bg-[#EF4444]/20 text-[#EF4444] rounded hover:bg-[#EF4444]/30 transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Modal para Crear/Editar Plantilla */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#1A1A1A] border-[#F5A623]/30 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogTitle className="text-2xl font-bold text-[#F5A623] mb-4">
            {editingId ? 'Editar Plantilla' : 'Nueva Plantilla'}
          </DialogTitle>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-white mb-2">Nombre de la Plantilla</Label>
              <Input
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Ej: Bienvenida, Recordatorio, etc."
                required
                className="bg-[#2A2A2A] border-white/10 text-white"
              />
            </div>

            <div>
              <Label className="text-white mb-2">Descripción</Label>
              <Input
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Breve descripción de la plantilla"
                required
                className="bg-[#2A2A2A] border-white/10 text-white"
              />
            </div>

            <div>
              <Label className="text-white mb-2">Asunto del Correo</Label>
              <Input
                value={formData.asunto}
                onChange={(e) => setFormData({ ...formData, asunto: e.target.value })}
                placeholder="Asunto del correo electrónico"
                required
                className="bg-[#2A2A2A] border-white/10 text-white"
              />
            </div>

            <div>
              <Label className="text-white mb-2">Variables Disponibles (separadas por coma)</Label>
              <Input
                value={formData.variables.join(', ')}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  variables: e.target.value.split(',').map(v => v.trim()).filter(v => v)
                })}
                placeholder="Ej: nombre, cedula, fecha_entrega"
                className="bg-[#2A2A2A] border-white/10 text-white"
              />
              <p className="text-[#666666] text-xs mt-1">
                Usa estas variables en el contenido como: {`{nombre}, {cedula}, etc.`}
              </p>
            </div>

            <div>
              <Label className="text-white mb-2">Contenido HTML</Label>
              <textarea
                value={formData.contenido_html}
                onChange={(e) => setFormData({ ...formData, contenido_html: e.target.value })}
                placeholder="Contenido HTML de la plantilla"
                required
                rows={10}
                className="w-full bg-[#2A2A2A] border border-white/10 text-white rounded-md p-3 font-mono text-sm"
              />
              <p className="text-[#666666] text-xs mt-1">
                Puedes usar HTML básico y las variables definidas arriba
              </p>
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <GoldButton type="button" variant="secondary" onClick={handleCloseModal}>
                Cancelar
              </GoldButton>
              <GoldButton type="submit">
                {editingId ? 'Actualizar' : 'Crear'} Plantilla
              </GoldButton>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
